import React, { useState } from "react";
import {
  IconCalendarEvent,
  IconPlus,
  IconSearch,
  IconClock,
  IconTool,
  IconCheck,
  IconAlertTriangle,
  IconCalendar,
  IconRefresh,
  IconListCheck
} from "@tabler/icons-react";
import {
  usePreventiveSchedules,
  useAssets,
  createPreventiveSchedule,
  triggerPreventiveWorkOrder
} from "../../controllers/assets.controller";
import toast from "react-hot-toast";

export default function AssetPreventiveTab() {
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: schedules, error, isLoading, mutate } = usePreventiveSchedules();
  const { data: assets } = useAssets();

  const [formData, setFormData] = useState({
    asset_id: "",
    title: "",
    frequency_type: "Monthly",
    frequency_interval: 1,
    next_due_date: new Date().toISOString().split("T")[0],
    assigned_technician: "In-house Maintenance Tech",
    checklist: "1. Clean filters & coils\n2. Check electrical connections\n3. Inspect lubrication & belts\n4. Verify operating temperatures",
    estimated_hours: 1.5
  });

  const filteredSchedules = Array.isArray(schedules)
    ? schedules.filter((s) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          s.title?.toLowerCase().includes(q) ||
          s.asset_code?.toLowerCase().includes(q) ||
          s.asset_name?.toLowerCase().includes(q) ||
          s.assigned_technician?.toLowerCase().includes(q)
        );
      })
    : [];

  const handleOpenCreateModal = () => {
    setFormData({
      asset_id: assets?.[0]?.id || "",
      title: "Quarterly HVAC Service & Filter Replacement",
      frequency_type: "Quarterly",
      frequency_interval: 3,
      next_due_date: new Date().toISOString().split("T")[0],
      assigned_technician: "In-house Maintenance Tech",
      checklist: "1. Inspect refrigerant levels\n2. Clean condenser coils\n3. Check thermostat calibration\n4. Replace air filters",
      estimated_hours: 2.0
    });
    setIsModalOpen(true);
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    if (!formData.asset_id) {
      toast.error("Please select an asset");
      return;
    }
    if (!formData.title) {
      toast.error("Please provide a schedule title");
      return;
    }

    try {
      await createPreventiveSchedule(formData);
      toast.success("Preventive Maintenance Schedule established!");
      setIsModalOpen(false);
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to create schedule");
    }
  };

  const handleTriggerWorkOrder = async (scheduleId, scheduleTitle) => {
    try {
      await triggerPreventiveWorkOrder(scheduleId);
      toast.success(`Generated maintenance work order for: ${scheduleTitle}`);
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to trigger work order");
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date(new Date().toISOString().split("T")[0]);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <IconCalendarEvent className="w-6 h-6 text-restro-green" />
            Preventive Maintenance (PM) Schedules
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Automate recurring servicing for chillers, generators, elevators, kitchen fryers, and guest room ACs.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <IconSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search schedules or assets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-restro-green w-64 text-gray-800 dark:text-gray-200"
            />
          </div>
          <button
            onClick={handleOpenCreateModal}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
          >
            <IconPlus className="w-4 h-4" />
            New PM Schedule
          </button>
        </div>
      </div>

      {/* Schedules Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600 dark:text-gray-300">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 uppercase text-xs font-semibold">
              <tr>
                <th className="px-6 py-4">Schedule Title</th>
                <th className="px-6 py-4">Asset</th>
                <th className="px-6 py-4">Frequency</th>
                <th className="px-6 py-4">Assigned Tech</th>
                <th className="px-6 py-4">Next Due Date</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="text-center py-10 text-gray-400">
                    Loading PM schedules...
                  </td>
                </tr>
              ) : filteredSchedules.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-400">
                    <IconCalendarEvent className="w-12 h-12 mx-auto mb-2 opacity-30 text-restro-green" />
                    No recurring maintenance schedules set up yet.
                  </td>
                </tr>
              ) : (
                filteredSchedules.map((s) => {
                  const overdue = isOverdue(s.next_due_date);
                  return (
                    <tr key={s.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-750 transition">
                      <td className="px-6 py-4">
                        <div className="font-bold text-gray-900 dark:text-white">{s.title}</div>
                        {s.checklist && (
                          <div className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                            <IconListCheck className="w-3.5 h-3.5" /> Checklist attached
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {s.asset_name || "Asset #" + s.asset_id}
                        </div>
                        <div className="text-xs text-gray-400 font-mono">
                          {s.asset_code || `AST-${s.asset_id}`}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
                          <IconRefresh className="w-3 h-3" />
                          {s.frequency_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs font-medium text-gray-800 dark:text-gray-200">
                        {s.assigned_technician || "Internal Tech"}
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className={`text-xs font-bold flex items-center gap-1 ${
                            overdue ? "text-rose-600 dark:text-rose-400" : "text-gray-900 dark:text-white"
                          }`}
                        >
                          {overdue && <IconAlertTriangle className="w-3.5 h-3.5 text-rose-500" />}
                          {s.next_due_date ? new Date(s.next_due_date).toLocaleDateString() : "-"}
                        </div>
                        {overdue && <div className="text-[10px] text-rose-500 font-semibold">OVERDUE</div>}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                            s.is_active
                              ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"
                              : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {s.is_active ? "Active" : "Paused"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleTriggerWorkOrder(s.id, s.title)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-lg shadow-sm transition"
                        >
                          <IconTool className="w-3.5 h-3.5" />
                          Trigger Work Order
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: New PM Schedule */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-xl overflow-hidden border border-gray-100 dark:border-gray-700 my-8">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <IconCalendarEvent className="w-5 h-5 text-restro-green" />
                Configure Preventive Maintenance Schedule
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateSchedule} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Schedule Title *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Monthly Deep Clean & Filter Check"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Target Equipment / Asset *
                </label>
                <select
                  value={formData.asset_id}
                  onChange={(e) => setFormData({ ...formData, asset_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  required
                >
                  <option value="">-- Choose asset --</option>
                  {assets?.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.asset_code} - {a.asset_name} ({a.location || a.room_number || "Main Property"})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Frequency Cycle *
                  </label>
                  <select
                    value={formData.frequency_type}
                    onChange={(e) => setFormData({ ...formData, frequency_type: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200 font-medium"
                  >
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Bi-Weekly">Bi-Weekly (Fortnightly)</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly (3 Months)</option>
                    <option value="Semi-Annually">Semi-Annually (6 Months)</option>
                    <option value="Annually">Annually (1 Year)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    First / Next Due Date *
                  </label>
                  <input
                    type="date"
                    value={formData.next_due_date}
                    onChange={(e) => setFormData({ ...formData, next_due_date: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Responsible Technician / Team
                  </label>
                  <input
                    type="text"
                    value={formData.assigned_technician}
                    onChange={(e) => setFormData({ ...formData, assigned_technician: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Estimated Duration (Hours)
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    value={formData.estimated_hours}
                    onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Maintenance Checklist & Verification Points
                </label>
                <textarea
                  rows="3"
                  value={formData.checklist}
                  onChange={(e) => setFormData({ ...formData, checklist: e.target.value })}
                  placeholder="Numbered checklist items for technician..."
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200 font-mono text-xs"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
                >
                  Save Schedule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
